import { defineStore } from 'pinia';
import DialerAPI from 'dashboard/api/campaigns/dialer';
import TwilioVoiceClient from 'dashboard/api/channel/voice/twilioVoiceClient';
import { useCallsStore } from './calls';

// Being in a campaign is a state of the person, not of the page they happen to have open. This
// store keeps that state alive across navigation and tells the server the browser is still here -
// a session nobody has heard from stops being offered calls, so the heartbeat is what lets an
// agent leave this screen and keep taking calls.
const HEARTBEAT_MS = 15000;
// A refreshed tab must not silently drop an agent out of the rotation: the campaign is remembered
// here and rejoined on the next boot.
const STORAGE_KEY = 'dialer_presence_campaign';

// Which tab holds the parked leg. Only one may: two tabs parking in turn would each kill the
// other's leg, and every turn is a customer handed to a line that just went away.
const LEASE_KEY = 'dialer_park_owner';
// How long a claim survives without being refreshed. Longer than a heartbeat, so the owner never
// loses it while it is alive; short enough that a crashed tab is replaced within one beat.
const LEASE_TTL = 40000;
const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// Parking again is cheap but not free - it is an API call, a token and a new Twilio leg - and a
// flapping network would otherwise do it in a loop.
const REPARK_COOLDOWN_MS = 5000;

// The leg is doing something that must not be pulled out from under it: a call in progress, or a
// bridge Twilio is about to run.
const BUSY_STATUSES = ['on_call', 'reserved'];

// Twilio reports a blocked or missing microphone as 31401, and the browser's own refusal arrives as
// a DOMException. It is the one join failure the agent can fix themselves, so it is worth telling
// apart from "something went wrong".
const MIC_ERROR_NAMES = [
  'NotAllowedError',
  'NotFoundError',
  'NotReadableError',
];
const isMicrophoneError = error =>
  error?.code === 31401 ||
  MIC_ERROR_NAMES.includes(error?.name) ||
  MIC_ERROR_NAMES.includes(error?.cause?.name);

const read = key => {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch (error) {
    return null;
  }
};

const write = (key, value) => {
  try {
    if (value) localStorage.setItem(key, JSON.stringify(value));
    else localStorage.removeItem(key);
  } catch (error) {
    // Private windows and blocked storage: presence does not survive a reload there, and every tab
    // considers itself the owner of the leg. The server hangs up the leg it replaced, so the worst
    // case is the same one tab that is parked now.
  }
};

const isLeaseMine = () => read(LEASE_KEY)?.tab === TAB_ID;

const leaseIsFree = () => {
  const claim = read(LEASE_KEY);
  return !claim || claim.tab === TAB_ID || Date.now() - claim.at > LEASE_TTL;
};

// Taken, then read back: two tabs can find the same dead claim in the same instant, and the one
// whose write landed last is the one that parks.
const claimLease = () => {
  if (!leaseIsFree()) return false;
  write(LEASE_KEY, { tab: TAB_ID, at: Date.now() });
  return isLeaseMine();
};

let heartbeatTimer = null;
let detachClientListeners = null;
let isParking = false;
let isLeaving = false;
let lastParkAt = 0;

export const useDialerPresenceStore = defineStore('dialerPresence', {
  state: () => ({
    campaignId: null,
    campaignTitle: '',
    sessionId: null,
    status: null,
    // Why the agent is not parked, when they are not: 'microphone' is theirs to fix, 'generic' is
    // ours. Null while the leg is up.
    parkError: null,
    // A break the agent asked for: out of the rotation, the panel still up to come back from.
    isPaused: false,
  }),

  getters: {
    isInCampaign: state => !!state.campaignId,
    // The heartbeat's status can be a beat behind; a handed-off call reaches the calls store first.
    isOnCall: state =>
      BUSY_STATUSES.includes(state.status) || useCallsStore().hasActiveCall,
  },

  actions: {
    startHeartbeat() {
      this.stopHeartbeat();
      heartbeatTimer = setInterval(() => this.ping(), HEARTBEAT_MS);
    },

    stopHeartbeat() {
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    },

    // The parked leg is meant to last the whole shift: Twilio redirects it into each customer's
    // conference and the TwiML brings it back, so it never disconnects between calls. A disconnect
    // or an error is therefore the leg being gone - a laptop that slept, a media failure, a token
    // that could not be renewed - and until the browser parks again the dialer is handing calls to
    // an empty chair.
    watchLeg() {
      if (detachClientListeners) return;

      const onLegLost = () => {
        if (isParking || isLeaving || !this.campaignId) return;
        this.repark();
      };

      TwilioVoiceClient.addEventListener('call:disconnected', onLegLost);
      TwilioVoiceClient.addEventListener('call:error', onLegLost);
      detachClientListeners = () => {
        TwilioVoiceClient.removeEventListener('call:disconnected', onLegLost);
        TwilioVoiceClient.removeEventListener('call:error', onLegLost);
        detachClientListeners = null;
      };
    },

    // Joining is what parks the leg, so it is also how the browser parks again: the server hands out
    // a fresh token and puts the session back to `connecting`, which keeps the dialer from choosing
    // this agent while their leg is on its way up.
    async join({ campaignId, campaignTitle }) {
      isParking = true;
      lastParkAt = Date.now();
      // A join the agent asked for takes the leg from whichever tab had it.
      write(LEASE_KEY, { tab: TAB_ID, at: Date.now() });

      try {
        const { data } = await DialerAPI.join(campaignId);
        const connection = await TwilioVoiceClient.park({
          token: data.token,
          sessionId: data.session_id,
        });
        this.isPaused = false;

        // Parking only counts once Twilio has run the TwiML and the leg is in its conference. The
        // status comes from the server, so ask again the moment that happens instead of leaving
        // "connecting" on screen until the next beat.
        connection?.once?.('accept', () => this.ping());
        // The event is the fast path; this covers a leg that was already up when we attached.
        setTimeout(() => this.ping(), 2000);

        this.campaignId = campaignId;
        this.campaignTitle = data.campaign_title || campaignTitle || '';
        this.sessionId = data.session_id;
        this.status = data.agents?.find(agent => agent.is_me)?.status || null;
        this.parkError = null;
        this.startHeartbeat();
        this.watchLeg();
        write(STORAGE_KEY, { campaignId, campaignTitle: this.campaignTitle });

        return data;
      } catch (error) {
        this.parkError = isMicrophoneError(error) ? 'microphone' : 'generic';
        throw error;
      } finally {
        isParking = false;
      }
    },

    // Back into the rotation after the leg went away. The agent is already in the campaign as far
    // as the server is concerned, so there is nothing to tell them - unless this keeps failing,
    // which is what `parkError` is for.
    async repark() {
      if (isParking || isLeaving || !this.campaignId || this.isPaused) return;
      if (BUSY_STATUSES.includes(this.status)) return;
      if (Date.now() - lastParkAt < REPARK_COOLDOWN_MS) return;
      // Another tab is holding the leg: it is the one that parks.
      if (!claimLease()) return;

      try {
        await this.join({
          campaignId: this.campaignId,
          campaignTitle: this.campaignTitle,
        });
      } catch (error) {
        // `parkError` carries the reason; the next heartbeat tries again.
      }
    },

    // Called once when the dashboard boots: an agent who refreshed the page is back in the
    // rotation without having to remember they were in a campaign.
    async restore() {
      if (this.campaignId) return;
      const saved = read(STORAGE_KEY);
      if (!saved?.campaignId) return;
      // On a break when the page went away: still on one after it comes back, not dialled for.
      if (saved.paused) {
        this.campaignId = saved.campaignId;
        this.campaignTitle = saved.campaignTitle || '';
        this.isPaused = true;
        return;
      }

      try {
        await this.join(saved);
      } catch (error) {
        write(STORAGE_KEY, null);
      }
    },

    async ping() {
      if (!this.campaignId || this.isPaused) return;
      if (isLeaseMine()) write(LEASE_KEY, { tab: TAB_ID, at: Date.now() });

      try {
        const { data } = await DialerAPI.heartbeat(this.campaignId);
        // The server dropped the session (the agent left from another tab): stop pretending - or,
        // when that tab only paused, go on a break along with it.
        if (!data.session_id) {
          if (read(STORAGE_KEY)?.paused) this.enterPause();
          else this.reset();
          return;
        }
        const me = data.agents?.find(agent => agent.is_me);
        this.status = me?.status || null;
        if (data.campaign_title) this.campaignTitle = data.campaign_title;

        // A leg can also die without the SDK saying a word - a suspended laptop, a tab the browser
        // froze. The server is the one that knows whether there is still something to hand a call
        // to, so its answer is the other trigger for parking again.
        if (!me?.parked || !TwilioVoiceClient.hasActiveConnection)
          this.repark();
      } catch (error) {
        // A missed beat is not a reason to leave; the next one either lands or the server pauses
        // this agent on its own.
      }
    },

    // A break: out of the rotation exactly the way leaving is - the server stops dialing for this
    // agent and calls off whatever was still ringing for them - but the panel stays, so coming back
    // is one click. Never in the middle of a call: the leg that goes is the one carrying it.
    async pause() {
      if (!this.campaignId || this.isPaused || this.isOnCall) return;

      const { campaignId } = this;
      isLeaving = true;
      TwilioVoiceClient.endClientCall();
      TwilioVoiceClient.destroyDevice();
      this.enterPause();

      try {
        await DialerAPI.leave(campaignId, { pause: true });
      } finally {
        isLeaving = false;
      }
    },

    // Back from the break through the same door as joining. A join that fails leaves the agent on
    // the break and tells the server so, rather than half in the campaign with no leg.
    async resume() {
      try {
        return await this.join({
          campaignId: this.campaignId,
          campaignTitle: this.campaignTitle,
        });
      } catch (error) {
        await DialerAPI.leave(this.campaignId, { pause: true }).catch(() => {});
        throw error;
      }
    },

    enterPause() {
      this.stopHeartbeat();
      detachClientListeners?.();
      if (isLeaseMine()) write(LEASE_KEY, null);
      this.isPaused = true;
      this.sessionId = null;
      this.status = null;
      this.parkError = null;
      write(STORAGE_KEY, {
        campaignId: this.campaignId,
        campaignTitle: this.campaignTitle,
        paused: true,
      });
    },

    async leave() {
      const campaignId = this.campaignId;
      isLeaving = true;
      this.reset();
      TwilioVoiceClient.endClientCall();
      TwilioVoiceClient.destroyDevice();

      try {
        if (campaignId) await DialerAPI.leave(campaignId);
      } finally {
        isLeaving = false;
      }
    },

    reset() {
      write(STORAGE_KEY, null);
      if (isLeaseMine()) write(LEASE_KEY, null);
      this.stopHeartbeat();
      detachClientListeners?.();
      this.campaignId = null;
      this.campaignTitle = '';
      this.sessionId = null;
      this.status = null;
      this.parkError = null;
      this.isPaused = false;
    },
  },
});
